// src/components/CreateAuthorTitleModal.jsx
// Independent Author Title Upload Modal - Title-first flow (mirrors Managed Author pattern)
// Authors upload their own work - no Publisher attestation required
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Upload, FileText, Loader2, Image, X, CheckCircle, BookOpen } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useNotification } from '@/contexts/NotificationContext';
import axios from 'axios';

// Import genre/theme constants
import { BOOK_TYPE_OPTIONS, getGenresForBookType, getThemesForGenre } from '../constants/genreThemes';
import { BACKEND_URL as backendUrl } from '@/config/api';

const CreateAuthorTitleModal = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  authorProfile 
}) => {
  const navigate = useNavigate();
  const { notifyError } = useNotification();
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  
  const [uploading, setUploading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    title_name: '',
    subtitle: '',
    isbn: '',
    book_type: '',
    genre: '',
    theme: '',
    purchase_url: '',
    is_promotional: false,
    description: ''
  });
  
  // File states
  const [manuscriptFile, setManuscriptFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  
  // Upload progress
  const [uploadProgress, setUploadProgress] = useState({ stage: '', percent: 0 });
  const [showProgress, setShowProgress] = useState(false);
  
  // Created IDs for callbacks
  const [createdTitleId, setCreatedTitleId] = useState(null);
  const [createdManuscriptId, setCreatedManuscriptId] = useState(null);
  
  const [errors, setErrors] = useState({});
  // backendUrl imported from centralized config as BACKEND_URL

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title_name: '',
        subtitle: '',
        isbn: '',
        book_type: '',
        genre: '',
        theme: '',
        purchase_url: '',
        is_promotional: false,
        description: ''
      });
      setManuscriptFile(null);
      setCoverFile(null);
      setCoverPreview(null);
      setErrors({});
      setShowProgress(false);
      setCreatedTitleId(null);
      setCreatedManuscriptId(null);
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.epub')) {
      notifyError('Please select an EPUB file');
      return;
    }
    
    if (file.size > 20 * 1024 * 1024) {
      notifyError('File size exceeds 20MB limit');
      return;
    }
    
    setManuscriptFile(file);
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      notifyError('Please select a valid image file (JPG, PNG, or WebP)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      notifyError('Cover image exceeds 5MB limit');
      return;
    }
    
    setCoverFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title_name.trim()) {
      newErrors.title_name = 'Title is required';
    }
    
    if (!manuscriptFile) {
      newErrors.manuscript = 'Manuscript file is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      notifyError('Please fix the errors above');
      return;
    }
    
    setUploading(true);
    setShowProgress(true);
    setUploadProgress({ stage: 'Creating title record...', percent: 10 });
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // STEP 1: Create title record
      const titlePayload = {
        title_name: formData.title_name,
        subtitle: formData.subtitle || null,
        description: formData.description || null,
        isbn: formData.isbn || null,
        book_type: formData.book_type || null,
        genre: formData.genre || null,
        theme: formData.theme || null,
        purchase_url: formData.purchase_url || null,
        is_promotional: formData.is_promotional
      };
      
      const titleResponse = await fetch(`${backendUrl}/api/author/titles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(titlePayload)
      });
      
      if (!titleResponse.ok) {
        const error = await titleResponse.json();
        throw new Error(error.detail || 'Failed to create title record');
      }
      
      const titleData = await titleResponse.json();
      const titleId = titleData.id;
      setCreatedTitleId(titleId);
      
      // STEP 2: Upload cover image if provided
      if (coverFile) {
        setUploadProgress({ stage: 'Uploading cover image...', percent: 20 });
        
        try {
          const coverFormData = new FormData();
          coverFormData.append('cover', coverFile);
          
          await fetch(`${backendUrl}/api/author/titles/${titleId}/cover`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: coverFormData
          });
        } catch (coverError) {
          console.warn('Cover upload error (non-fatal):', coverError.message);
        }
      }
      
      setUploadProgress({ stage: 'Uploading manuscript...', percent: 30 });
      
      // STEP 3: Upload manuscript and trigger ingestion
      const manuscriptFormData = new FormData();
      manuscriptFormData.append('file', manuscriptFile);
      manuscriptFormData.append('upload_format', 'epub');
      
      const uploadResponse = await fetch(`${backendUrl}/api/author/titles/${titleId}/upload-manuscript`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: manuscriptFormData
      });
      
      if (!uploadResponse.ok) {
        const error = await uploadResponse.json();
        throw new Error(error.detail || 'Failed to upload manuscript');
      }
      
      const uploadData = await uploadResponse.json();
      
      setUploadProgress({ stage: 'Processing complete!', percent: 100 });
      
      const manuscriptId = uploadData.manuscript_id;
      setCreatedManuscriptId(manuscriptId);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('[SUCCESS] ' + 'Title created successfully!');
      
      // Call success callback with manuscript data
      if (onSuccess) {
        onSuccess({
          titleId,
          manuscriptId,
          titleName: formData.title_name,
          wordCount: uploadData.word_count,
          chapterCount: uploadData.chapter_count
        });
      }
      
      setUploading(false);
      setShowProgress(false);
      onClose();
      
    } catch (error) {
      console.error('Error in upload flow:', error);
      notifyError(error.message || 'Upload failed');
      setShowProgress(false);
      setUploading(false);
    }
  };

  const authorDisplayName = authorProfile?.pen_name || 
    `${authorProfile?.first_name || ''} ${authorProfile?.last_name || ''}`.trim() || 
    'Author';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !uploading && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-800">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-purple-400" />
            Create New Title
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Upload your manuscript as <span className="text-purple-400 font-medium">{authorDisplayName}</span>
          </DialogDescription>
        </DialogHeader>

        {showProgress ? (
          <div className="py-8">
            <div className="flex items-center gap-4 mb-4">
              <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
              <span className="text-white">{uploadProgress.stage}</span>
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${uploadProgress.percent}%` }}
              />
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title Information */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="title_name" className="text-slate-300">
                  Title <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="title_name"
                  value={formData.title_name}
                  onChange={(e) => handleInputChange('title_name', e.target.value)}
                  placeholder="Enter your book title"
                  className={`bg-slate-800 border-slate-700 text-white ${errors.title_name ? 'border-red-500' : ''}`}
                />
                {errors.title_name && (
                  <p className="text-red-400 text-sm mt-1">{errors.title_name}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="subtitle" className="text-slate-300">Subtitle</Label>
                <Input
                  id="subtitle"
                  value={formData.subtitle}
                  onChange={(e) => handleInputChange('subtitle', e.target.value)}
                  placeholder="Optional subtitle"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="isbn" className="text-slate-300">ISBN</Label>
                  <Input
                    id="isbn"
                    value={formData.isbn}
                    onChange={(e) => handleInputChange('isbn', e.target.value)}
                    placeholder="Optional"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                
                <div>
                  <Label htmlFor="book_type" className="text-slate-300">Book Type</Label>
                  <select
                    id="book_type"
                    value={formData.book_type}
                    onChange={(e) => {
                      handleInputChange('book_type', e.target.value);
                      handleInputChange('genre', '');
                      handleInputChange('theme', '');
                    }}
                    className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white text-sm"
                  >
                    <option value="">Select type...</option>
                    {BOOK_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="genre" className="text-slate-300">Genre</Label>
                  <select
                    id="genre"
                    value={formData.genre}
                    onChange={(e) => {
                      handleInputChange('genre', e.target.value);
                      handleInputChange('theme', '');
                    }}
                    disabled={!formData.book_type}
                    className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white text-sm disabled:opacity-50"
                  >
                    <option value="">Select genre...</option>
                    {getGenresForBookType(formData.book_type).map(g => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="theme" className="text-slate-300">Theme</Label>
                  <select
                    id="theme"
                    value={formData.theme}
                    onChange={(e) => handleInputChange('theme', e.target.value)}
                    disabled={!formData.genre}
                    className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white text-sm disabled:opacity-50"
                  >
                    <option value="">Select theme...</option>
                    {getThemesForGenre(formData.genre).map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="description" className="text-slate-300">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Brief description of your book..."
                  rows={3}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            {/* File Uploads Section */}
            <div className="space-y-4 pt-4 border-t border-slate-800">
              <h3 className="text-sm font-semibold text-slate-300">Files</h3>
              
              {/* Cover Image */}
              <div className="flex items-start gap-4">
                {coverPreview ? (
                  <div className="relative">
                    <img 
                      src={coverPreview} 
                      alt="Cover preview" 
                      className="w-20 h-28 object-cover rounded-lg border border-slate-700"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setCoverFile(null);
                        setCoverPreview(null);
                      }}
                      className="absolute -top-2 -right-2 p-1 bg-red-600 rounded-full text-white hover:bg-red-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-28 bg-slate-800 rounded-lg border border-dashed border-slate-700 flex items-center justify-center">
                    <Image className="w-6 h-6 text-slate-600" />
                  </div>
                )}
                
                <div className="flex-1">
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleCoverSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => coverInputRef.current?.click()}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {coverPreview ? 'Change Cover' : 'Upload Cover'}
                  </Button>
                  <p className="text-xs text-slate-500 mt-1">
                    Optional. JPG, PNG, or WebP. Max 5MB.
                  </p>
                </div>
              </div>

              {/* Manuscript File */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".epub"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {manuscriptFile ? (
                  <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg">
                    <FileText className="w-6 h-6 text-green-400" />
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{manuscriptFile.name}</p>
                      <p className="text-xs text-slate-400">
                        {(manuscriptFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setManuscriptFile(null)}
                      className="p-1 text-slate-400 hover:text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={`w-full p-6 border-2 border-dashed rounded-lg text-center transition-colors ${
                      errors.manuscript 
                        ? 'border-red-500 bg-red-950/20' 
                        : 'border-slate-700 hover:border-purple-500 bg-slate-800/50'
                    }`}
                  >
                    <Upload className="w-8 h-8 mx-auto mb-2 text-slate-500" />
                    <p className="text-white text-sm font-medium mb-1">Click to upload EPUB *</p>
                    <p className="text-xs text-slate-500">Maximum file size: 20MB</p>
                  </button>
                )}
                
                {errors.manuscript && (
                  <p className="text-red-400 text-sm mt-1">{errors.manuscript}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={uploading}
                className="border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={uploading}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Create & Upload
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateAuthorTitleModal;
